import { useState, useEffect } from 'react'
import { GameState, Mugong, Item } from '../store/types'
import { apiActionStart, apiActionCancel, apiAutoPotionSet, apiAutoPotionQuery } from '../server/ServiceCall'

const GRADE_COLOR: Record<string, string> = {
  COMMON:'#a0a0a0', UNCOMMON:'#60c060', RARE:'#4090e0',
  EPIC:'#a060e0', LEGEND:'#e0a030', MYTH:'#e04060',
}
const ELEMENT_COLOR: Record<string, string> = {
  '木':'#60a040', '火':'#e06030', '土':'#c0a040', '金':'#c8c840',
  '水':'#4080c0', '氷':'#60c0e0', '毒':'#80c040', '正':'#80a0e0',
  '邪':'#a060a0', '魔':'#c04040',
}
const CATEGORY_KO: Record<string, string> = {
  '心法':'심법', '步法':'보법', '輕功':'경공', '拳法':'권법',
  '劍法':'검법', '刀法':'도법', '掌法':'장법',
}

interface AutoPotionSetting {
  hpItemId:    string | null
  hpItemName:  string | null
  hpThreshold: number
}

interface Props {
  gs:         GameState
  token:      string
  onRefresh:  () => void
  onForceLogout: () => void
}

export default function TabTrain({ gs, token, onRefresh, onForceLogout }: Props) {
  const [selected,    setSelected]    = useState<Mugong | null>(null)
  const [catTab,      setCatTab]      = useState<string>('전체')
  const [busy,        setBusy]        = useState(false)
  const [msg,         setMsg]         = useState('')
  const [msgOk,       setMsgOk]       = useState(false)
  const [autoPotion,  setAutoPotion]  = useState<AutoPotionSetting>({
    hpItemId: null, hpItemName: null, hpThreshold: 30,
  })
  const [showAutoPotion, setShowAutoPotion] = useState(false)
  const [apBusy,         setApBusy]         = useState(false)

  const isTraining   = gs.char.activeAction === 'TRAIN'
  const isRecovering = gs.char.activeAction === 'RECOVER'
  const isAnyAction  = gs.char.activeAction !== null

  const hpPct        = gs.char.maxHp > 0 ? Math.round(gs.char.hp / gs.char.maxHp * 100) : 0
  const isAlreadyFull = gs.char.hp >= gs.char.maxHp

  const consumeItems: Item[] = gs.inventory.filter(
    it => it.category === 'CONSUME' && it.hpBonus > 0
  )

  function showMsg(m: string, ok = false) {
    setMsg(m); setMsgOk(ok)
    setTimeout(() => setMsg(''), 3000)
  }

  useEffect(() => {
    if (!showAutoPotion) return
    apiAutoPotionQuery(token)
      .then(res => {
        if (res.resultCode === '00' && res.resultData) {
          const d = JSON.parse(res.resultData)
          setAutoPotion({
            hpItemId:    d.HP_ITEM_ID    || null,
            hpItemName:  d.HP_ITEM_NAME  || null,
            hpThreshold: Number(d.HP_THRESHOLD) || 30,
          })
        }
      })
      .catch(() => {})
  }, [showAutoPotion, token])

  async function doStartTrain(mg: Mugong) {
    setBusy(true)
    try {
      const res = await apiActionStart(token, 'TRAIN', mg.mugongId)
      if (res.resultCode === '97') { onForceLogout(); return }
      showMsg(res.resultMessage, res.resultCode === '00')
      if (res.resultCode === '00') onRefresh()
    } catch { showMsg('서버 오류') }
    finally { setBusy(false) }
  }

  async function doStartRecover() {
    setBusy(true)
    try {
      const res = await apiActionStart(token, 'RECOVER', '')
      if (res.resultCode === '97') { onForceLogout(); return }
      showMsg(res.resultMessage, res.resultCode === '00')
      if (res.resultCode === '00') onRefresh()
    } catch { showMsg('서버 오류') }
    finally { setBusy(false) }
  }

  async function doCancel() {
    setBusy(true)
    try {
      const res = await apiActionCancel(token)
      showMsg(res.resultMessage, res.resultCode === '00')
      if (res.resultCode === '00') onRefresh()
    } catch { showMsg('서버 오류') }
    finally { setBusy(false) }
  }

  async function saveAutoPotion(itemId: string | null, threshold: number) {
    setApBusy(true)
    try {
      const res = await apiAutoPotionSet(token, 'HP', itemId ?? '', threshold)
      showMsg(res.resultMessage, res.resultCode === '00')
    } catch { showMsg('서버 오류') }
    finally { setApBusy(false) }
  }

  const starExpMult = 100
  function nextStarExp(mg: Mugong) { return starExpMult * mg.star }
  function expPct(mg: Mugong) { return Math.min(100, Math.round((mg.exp / nextStarExp(mg)) * 100)) }

  return (
    <div className="tab-action">

      {msg && <div className={`inven-msg${msgOk ? ' ok' : ''}`}>{msg}</div>}

      {/* 운기행공 중 */}
      {isRecovering && (
        <section className="card-section action-active">
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <div>
              <div className="card-title" style={{ color:'#50c8e0' }}>운기행공 중</div>
              <div style={{ fontSize:12, color:'#8ab8c8', marginTop:2 }}>
                체력을 회복하고 있습니다
              </div>
            </div>
            <button className="action-btn drop" disabled={busy} onClick={doCancel}>중단</button>
          </div>
          <div style={{ marginTop:8 }}>
            <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, marginBottom:2 }}>
              <span style={{ color:'#e05050' }}>체력</span>
              <span style={{ color:'#c8a0a0' }}>{gs.char.hp} / {gs.char.maxHp}</span>
            </div>
            <div style={{ height:6, background:'#2a1010', borderRadius:3 }}>
              <div style={{ height:'100%', width:`${hpPct}%`, background:'#c04040', borderRadius:3, transition:'width 0.3s' }} />
            </div>
          </div>
        </section>
      )}

      {/* 수련 중 */}
      {isTraining && (() => {
        const mg = gs.mugongs.find(m => m.mugongId === gs.char.activeTarget)
        return (
          <section className="card-section action-active">
            <div className="card-title" style={{ color:'#60c060' }}>수련 중</div>
            <div style={{ fontSize:13, color:'#c8b890', marginBottom:4 }}>
              {mg?.name ?? gs.char.activeTarget}
            </div>
            {mg && (
              <div style={{ fontSize:12, color:'#a09060', marginBottom:8 }}>
                <span style={{ color:'#e0a030', marginRight:8 }}>{mg.star}성</span>
                <span>경험치 {mg.exp}</span>
              </div>
            )}
            <button className="action-btn drop" style={{ marginTop:4 }}
              disabled={busy} onClick={doCancel}>수련 취소</button>
          </section>
        )
      })()}

      {isAnyAction && !isTraining && !isRecovering && (
        <section className="card-section action-busy">
          <div style={{ color:'#c8820a', fontSize:13 }}>
            현재 다른 행동 중 ({gs.char.activeAction === 'HUNT' ? '사냥' : gs.char.activeAction})
          </div>
          <button className="action-btn drop" style={{ marginTop:8 }}
            disabled={busy} onClick={doCancel}>행동 취소</button>
        </section>
      )}

      {/* 운기행공 */}
      {!isAnyAction && (
        <section className="card-section">
          <div className="card-title" style={{ color:'#50c8e0' }}>운기행공</div>
          <div style={{ marginBottom:8 }}>
            <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, marginBottom:2 }}>
              <span style={{ color:'#e05050' }}>체력</span>
              <span style={{ color:'#c8a0a0' }}>{gs.char.hp} / {gs.char.maxHp} ({hpPct}%)</span>
            </div>
            <div style={{ height:6, background:'#2a1010', borderRadius:3 }}>
              <div style={{ height:'100%', width:`${hpPct}%`, background:'#c04040', borderRadius:3 }} />
            </div>
          </div>
          {isAlreadyFull ? (
            <div style={{ fontSize:12, color:'#60a060', textAlign:'center', padding:'4px 0' }}>
              체력이 가득 찼습니다
            </div>
          ) : (
            <button className="action-btn equip" style={{ width:'100%' }}
              disabled={busy} onClick={doStartRecover}>
              운기행공 시작
            </button>
          )}
        </section>
      )}

      {/* 자동 소모품 설정 */}
      <section className="card-section">
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', cursor:'pointer' }}
          onClick={() => setShowAutoPotion(v => !v)}>
          <div className="card-title">자동 소모품 설정</div>
          <span style={{ fontSize:11, color:'#706050' }}>{showAutoPotion ? '▲' : '▼'}</span>
        </div>
        {showAutoPotion && (
          <div style={{ marginTop:8, display:'flex', flexDirection:'column', gap:12 }}>
            <div>
              <div style={{ fontSize:12, color:'#e05050', marginBottom:4 }}>체력 자동 소모품</div>
              <div style={{ fontSize:11, color:'#605040', marginBottom:6 }}>
                체력이 아래 수치(%) 이하일 때 자동 사용 (사냥 턴 종료 시, 1개)
              </div>
              <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:6 }}>
                <span style={{ fontSize:11, color:'#706050', minWidth:40 }}>소모품</span>
                <select
                  style={{ flex:1, background:'#1a1208', color:'#d0c090', border:'1px solid #4a3820', borderRadius:3, padding:'3px 6px', fontSize:12 }}
                  value={autoPotion.hpItemId ?? ''}
                  onChange={e => setAutoPotion(p => ({ ...p, hpItemId: e.target.value || null, hpItemName: null }))}
                >
                  <option value="">-- 없음 --</option>
                  {consumeItems.map(it => (
                    <option key={it.invId} value={it.itemId}>
                      {it.name} (체력+{it.hpBonus}) ×{it.qty}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:6 }}>
                <span style={{ fontSize:11, color:'#706050', minWidth:40 }}>임계치</span>
                <input
                  type="number" min={1} max={99}
                  style={{ width:60, background:'#1a1208', color:'#d0c090', border:'1px solid #4a3820', borderRadius:3, padding:'3px 6px', fontSize:12 }}
                  value={autoPotion.hpThreshold}
                  onChange={e => setAutoPotion(p => ({ ...p, hpThreshold: Math.max(1, Math.min(99, Number(e.target.value))) }))}
                />
                <span style={{ fontSize:11, color:'#706050' }}>% 이하일 때</span>
              </div>
              <button className="action-btn equip" style={{ width:'100%', fontSize:11 }}
                disabled={apBusy}
                onClick={() => saveAutoPotion(autoPotion.hpItemId, autoPotion.hpThreshold)}>
                체력 자동소모품 저장
              </button>
            </div>
          </div>
        )}
      </section>

      {/* 무공 수련 목록 */}
      {gs.mugongs.length === 0 ? (
        <div className="mugong-empty">
          습득한 무공이 없습니다.<br/>
          <span style={{ fontSize:11 }}>무공 탭에서 비급을 사용해 무공을 습득하세요.</span>
        </div>
      ) : (() => {
        const GRADE_ORDER: Record<string, number> = {
          MYTH:0, LEGEND:1, EPIC:2, RARE:3, UNCOMMON:4, COMMON:5,
        }
        const CAT_ORDER = ['心法','劍法','刀法','掌法','拳法','指法','步法','輕功']
        const sorted = [...gs.mugongs].sort((a, b) =>
          (GRADE_ORDER[a.grade] ?? 9) - (GRADE_ORDER[b.grade] ?? 9)
        )
        const presentCats = CAT_ORDER.filter(c => sorted.some(m => m.category === c))
        const seenCats = sorted.reduce<string[]>((acc, m) => acc.includes(m.category) ? acc : [...acc, m.category], [])
        const otherCats   = seenCats.filter(c => !CAT_ORDER.includes(c))
        const tabs        = ['전체', ...presentCats, ...otherCats]
        const visible     = catTab === '전체' ? sorted : sorted.filter(m => m.category === catTab)

        return (
          <section className="card-section">
            <div className="card-title">수련할 무공 선택</div>

            <div className="train-cat-tabs">
              {tabs.map(tab => (
                <button
                  key={tab}
                  className={`train-cat-tab${catTab === tab ? ' active' : ''}`}
                  onClick={() => { setCatTab(tab); setSelected(null) }}
                >
                  {CATEGORY_KO[tab] ?? tab}
                </button>
              ))}
            </div>

            <div className="mugong-list">
              {visible.map(mg => {
                const pct      = expPct(mg)
                const isActive = gs.char.activeTarget === mg.mugongId && isTraining
                return (
                  <div key={mg.mugongId}
                    className={`mugong-item${selected?.mugongId===mg.mugongId?' selected':''}${isActive?' active-training':''}`}
                    onClick={() => !isAnyAction && setSelected(selected?.mugongId===mg.mugongId ? null : mg)}
                  >
                    <div className="mugong-name-row">
                      {mg.element && (
                        <span className="mugong-element" style={{ background: ELEMENT_COLOR[mg.element] ?? '#606060' }}>
                          {mg.element}
                        </span>
                      )}
                      <span className="mugong-name" style={{ color: GRADE_COLOR[mg.grade] }}>{mg.name}</span>
                      <span style={{ fontSize:10, color:'#806050', marginLeft:4 }}>
                        {CATEGORY_KO[mg.category] ?? mg.category}
                      </span>
                      <div className="mugong-stars" style={{ marginLeft:'auto' }}>
                        {Array.from({ length: mg.maxStar }).map((_, i) => (
                          <span key={i} className={`star${i < mg.star ? ' on' : ''}`}>★</span>
                        ))}
                      </div>
                    </div>

                    <div style={{ marginTop:4 }}>
                      <div style={{ display:'flex', justifyContent:'space-between', fontSize:10, color:'#706050', marginBottom:2 }}>
                        <span>경험치 {mg.exp} / {nextStarExp(mg)}</span>
                        <span>{pct}%</span>
                      </div>
                      <div className="category-bar-bg">
                        <div className="category-bar-fill" style={{ width:`${pct}%`, background:'#4060a0' }} />
                      </div>
                    </div>

                    <div className="mugong-bonus-row" style={{ marginTop:4 }}>
                      {mg.atkBonus  > 0 && <span>공 +{mg.atkBonus * mg.star}</span>}
                      {mg.defBonus  > 0 && <span>방 +{mg.defBonus * mg.star}</span>}
                      {mg.hpBonus   > 0 && <span>체 +{mg.hpBonus  * mg.star}</span>}
                      {isActive && <span style={{ color:'#60c060', marginLeft:'auto' }}>수련 중</span>}
                    </div>

                    {selected?.mugongId === mg.mugongId && !isAnyAction && (
                      <button className="action-btn equip" style={{ marginTop:8, width:'100%' }}
                        disabled={busy} onClick={() => doStartTrain(mg)}>
                        이 무공 수련 시작
                      </button>
                    )}
                  </div>
                )
              })}
            </div>
          </section>
        )
      })()}

    </div>
  )
}
